import { useCallback, useEffect, useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Edit from '@mui/icons-material/Edit';
import Add from '@mui/icons-material/Add';
import { FieldPanel, FormHeader, LineBreak, tooltipStyles } from '../../TemplateForm/styles';
import { abyssColors } from '@/theme/colors';
import { containedButtonStyle } from '@/theme';
import {
  AllLogsTemplatesByTypeDocument,
  CardConfig,
  Equation,
  ExpressionType,
  GetVersionsDocument,
  LogTemplateType,
  LogsTemplateQuery,
  SortTypeEnum,
  SectionUnitsEnum,
  TableCellStyleEnum,
  TemplateFieldTypeEnum,
  TextFormatEnum,
  useUpdateLogsTemplateMutation,
  LogsTemplateDocument,
  LogsTemplateField,
  useAllLogsTemplatesByTypeQuery,
  useLogsTemplateQuery,
} from '@/__generated__/graphql';
import {
  keyFromTitle,
  useSnackBarMessage,
  cleanEquations,
  cleanCardConfigs,
  removeTypename,
} from '../../utils';
import { generateMongoObjectId } from '@/utils/databaseHelpers';
import { EmptyStatePlaceholder } from '@/components/Analysis/shared/EmptyStatePlaceholder';
import { BasicTabs } from '@/components/shared/BasicTabs';
import { CardsConfigTab } from './CardsConfigTab';
import { EquationsConfigTab } from './EquationsConfigTab';
import { FloatingLine, workpackTabStyles } from '../styles';
import { formatEnumForDisplay } from '@/utils';

export type DerivedFieldsFormProps = {
  structureId: string;
  hostLogsTemplateId: string | null | undefined;
  hostLogsTemplate: LogsTemplateQuery['logsTemplate'] | null | undefined;
  hostLogTemplateType: LogTemplateType;
};

export const DerivedFieldsForm = ({
  structureId,
  hostLogsTemplateId,
  hostLogsTemplate,
  hostLogTemplateType,
}: DerivedFieldsFormProps) => {
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [cards, setCards] = useState<CardConfig[]>([]);
  const [equations, setEquations] = useState<Equation[]>([]);
  const [originalCards, setOriginalCards] = useState<CardConfig[]>([]);
  const [originalEquations, setOriginalEquations] = useState<Equation[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [equationDerivedFieldConfigByIndex, setEquationDerivedFieldConfigByIndex] = useState<
    Record<number, { fieldUnit: SectionUnitsEnum | null; displayInLogAggregation: boolean }>
  >({});

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const templateLabel = formatEnumForDisplay(hostLogTemplateType);

  const [updateLogsTemplateForDerivedField] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
    },
  });

  const availableEquations = useMemo(() => {
    return equations.map((equation) => ({
      key: equation.key,
      name: equation.name || equation.key,
    }));
  }, [equations]);

  const { data: indirectPobTemplatesData } = useAllLogsTemplatesByTypeQuery({
    variables: {
      input: {
        structureId,
        type: LogTemplateType.IndirectPobTemplate,
      },
    },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const crossIndirectPobTemplateId = indirectPobTemplatesData?.allLogsTemplates?.at(0)?.id;

  const { data: crossIndirectPobTemplateData } = useLogsTemplateQuery({
    variables: { logsTemplateId: crossIndirectPobTemplateId ?? '' },
    skip: !crossIndirectPobTemplateId,
    fetchPolicy: 'cache-first',
  });

  const { data: worklogsTemplatesData } = useAllLogsTemplatesByTypeQuery({
    variables: {
      input: {
        structureId,
        type: LogTemplateType.WorklogTemplate,
      },
    },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const crossWorklogsTemplateId = worklogsTemplatesData?.allLogsTemplates?.at(0)?.id;

  const { data: crossWorklogsTemplateData } = useLogsTemplateQuery({
    variables: { logsTemplateId: crossWorklogsTemplateId ?? '' },
    skip: !crossWorklogsTemplateId,
    fetchPolicy: 'cache-first',
  });

  const { data: costLogTemplatesData } = useAllLogsTemplatesByTypeQuery({
    variables: {
      input: {
        structureId,
        type: LogTemplateType.CostLogTemplate,
      },
    },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  const crossCostLogTemplateId = costLogTemplatesData?.allLogsTemplates?.at(0)?.id;

  const { data: crossCostLogTemplateData } = useLogsTemplateQuery({
    variables: { logsTemplateId: crossCostLogTemplateId ?? '' },
    skip: !crossCostLogTemplateId,
    fetchPolicy: 'cache-first',
  });

  const crossIndirectPobFields = useMemo(
    () =>
      crossIndirectPobTemplateData?.logsTemplate?.fields?.filter((field) => field !== null) ?? [],
    [crossIndirectPobTemplateData?.logsTemplate?.fields]
  );

  const crossWorklogFields = useMemo(
    () => crossWorklogsTemplateData?.logsTemplate?.fields?.filter((field) => field !== null) ?? [],
    [crossWorklogsTemplateData?.logsTemplate?.fields]
  );

  const crossCostLogFields = useMemo(
    () => crossCostLogTemplateData?.logsTemplate?.fields?.filter((field) => field !== null) ?? [],
    [crossCostLogTemplateData?.logsTemplate?.fields]
  );

  const indirectPobFieldKeySet = useMemo(() => {
    return new Set(
      crossIndirectPobFields.map((field) => field?.key).filter((key): key is string => Boolean(key))
    );
  }, [crossIndirectPobFields]);

  const worklogFieldKeySet = useMemo(() => {
    return new Set(
      crossWorklogFields.map((field) => field?.key).filter((key): key is string => Boolean(key))
    );
  }, [crossWorklogFields]);

  const costLogFieldKeySet = useMemo(() => {
    return new Set(
      crossCostLogFields.map((field) => field?.key).filter((key): key is string => Boolean(key))
    );
  }, [crossCostLogFields]);

  const availableFieldKeys = useMemo(() => {
    const keys = new Set<string>();
    const ordered: string[] = [];
    const addKey = (key: string | null | undefined) => {
      if (key && !keys.has(key)) {
        keys.add(key);
        ordered.push(key);
      }
    };
    for (const field of hostLogsTemplate?.fields ?? []) {
      addKey(field?.key ?? undefined);
    }
    for (const field of crossIndirectPobFields) {
      addKey(field.key ?? undefined);
    }
    for (const field of crossWorklogFields) {
      addKey(field.key ?? undefined);
    }
    for (const field of crossCostLogFields) {
      addKey(field.key ?? undefined);
    }
    return ordered;
  }, [hostLogsTemplate?.fields, crossIndirectPobFields, crossWorklogFields, crossCostLogFields]);

  const availableFieldsForEquations = useMemo(() => {
    const byKey = new Map<string, LogsTemplateField>();

    for (const field of hostLogsTemplate?.fields ?? []) {
      if (!field?.key) continue;
      byKey.set(field.key, field);
    }

    for (const field of crossIndirectPobFields) {
      if (!field.key) continue;
      byKey.set(field.key, {
        ...field,
        name: `${field.name} (Indirect POB)`,
      });
    }

    for (const field of crossWorklogFields) {
      if (!field.key) continue;
      byKey.set(field.key, {
        ...field,
        name: `${field.name} (Work log)`,
      });
    }

    for (const field of crossCostLogFields) {
      if (!field.key) continue;
      byKey.set(field.key, {
        ...field,
        name: `${field.name} (Cost log)`,
      });
    }

    return [...byKey.values()];
  }, [hostLogsTemplate?.fields, crossIndirectPobFields, crossWorklogFields, crossCostLogFields]);

  // Only hydrate from the server in ReadOnly. In Edit, Apollo can deliver a new
  // `hostLogsTemplate` reference (e.g. refetched allLogsTemplates / logsTemplate for
  // cost log cross-queries); resetting here would wipe unsaved cards/equations and
  // remount the empty state — especially visible for cost log templates.
  useEffect(() => {
    if (!hostLogsTemplate || mode !== 'ReadOnly') return;

    const filteredCards = hostLogsTemplate.cardsConfig?.filter((config) => config !== null) || [];
    const filteredEquations =
      hostLogsTemplate.equations?.filter((equation) => equation !== null) || [];

    setCards(filteredCards);
    setEquations(filteredEquations);
    setOriginalCards(filteredCards);
    setOriginalEquations(filteredEquations);
  }, [hostLogsTemplate, mode]);

  const handleCardsChange = useCallback((newCards: CardConfig[]) => {
    setCards(newCards);
  }, []);

  const handleEquationsChange = useCallback((newEquations: Equation[]) => {
    setEquations(newEquations);
  }, []);

  const handleCreateNewField = useCallback(
    (
      fieldKey: string,
      equationKey: string,
      config?: { fieldUnit?: SectionUnitsEnum | null; displayInLogAggregation?: boolean }
    ) => {
      if (!hostLogsTemplate?.fields || !hostLogsTemplateId) return;

      const existingField = hostLogsTemplate.fields.find((field) => field.key === fieldKey);
      if (existingField) return;

      const newField: LogsTemplateField = {
        id: `newField_${Date.now()}`,
        key: fieldKey,
        name: fieldKey.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase()),
        type: TemplateFieldTypeEnum.Input,
        options: [],
        isReadOnly: false,
        isRequired: false,
        displayInReports: true,
        displayInReportFilters: false,
        fieldUnit: config?.fieldUnit ?? SectionUnitsEnum.Na,
        displayInLogAggregation: config?.displayInLogAggregation ?? true,
        isDerived: true,
        derivedEquationKey: equationKey,
        serverSide: false,
        displayConfig: {
          id: `display_${Date.now()}`,
          textFormat: TextFormatEnum.None,
          isSortable: true,
          sortType: SortTypeEnum.Number,
          type: TableCellStyleEnum.Text,
        },
      };

      const updatedFields = [...hostLogsTemplate.fields, newField];

      updateLogsTemplateForDerivedField({
        variables: {
          input: {
            structureId,
            templateId: hostLogsTemplateId,
            fields: updatedFields,
            lastModifiedFor: 'ADD_DERIVED_FIELD',
          },
        },
        refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
      });

      showSnackBar(`Created new derived field: ${newField.name}`, 'success');
    },
    [
      hostLogsTemplate?.fields,
      hostLogsTemplateId,
      structureId,
      updateLogsTemplateForDerivedField,
      showSnackBar,
    ]
  );

  const createFirstCard = useCallback(() => {
    const newCard: CardConfig = {
      id: generateMongoObjectId(),
      key: keyFromTitle('New KPI Card'),
      title: 'New KPI Card',
      order: 1,
      icon: 'unknown',
      equationKey: availableEquations[0]?.key || null,
      aggregation: null,
      subtitleConfig: null,
    };
    setCards([newCard]);
    setMode('Edit');
  }, [availableEquations]);

  const createFirstEquation = useCallback(() => {
    const newEquation: Equation = {
      key: keyFromTitle('New Equation'),
      name: 'New Equation',
      variables: [],
      expression: {
        type: ExpressionType.Number,
        value: 0,
      },
    };
    setEquations([newEquation]);
    setMode('Edit');
  }, []);

  const [saveKpiConfig, { loading: isLoading }] = useUpdateLogsTemplateMutation({
    refetchQueries: [AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    onCompleted: (response) => {
      if (!response?.updateLogsTemplate) {
        showSnackBar(`An error occurred while updating ${templateLabel}`, 'error');
        return;
      }
      showSnackBar(`${templateLabel} KPI configuration saved successfully`, 'success');
      setMode('ReadOnly');
    },
    onError: (error) => {
      console.error(error);
      showSnackBar(error.message, 'error');
      setMode('ReadOnly');
    },
  });

  const handleCancel = useCallback(() => {
    setCards([...originalCards]);
    setEquations([...originalEquations]);
    setMode('ReadOnly');
  }, [originalCards, originalEquations]);

  const handleSaveKpiConfig = useCallback(() => {
    const equationKeys = equations
      .map((equation) => equation.key?.trim())
      .filter((key): key is string => Boolean(key));
    const equationKeyCounts = new Map<string, number>();
    for (const key of equationKeys) {
      equationKeyCounts.set(key, (equationKeyCounts.get(key) ?? 0) + 1);
    }
    const hasDuplicateEquationKey = [...equationKeyCounts.values()].some((count) => count > 1);
    if (hasDuplicateEquationKey) {
      showSnackBar(
        'Equation keys must be unique. Please rename the equation(s) with duplicate keys.',
        'error'
      );
      return;
    }

    const cleanedEquations = cleanEquations(equations);
    const cleanedCards = cleanCardConfigs(cards);

    const titleCaseFromKey = (key: string) =>
      key
        .replaceAll('_', ' ')
        .trim()
        .replaceAll(/\s+/g, ' ')
        .replaceAll(/\b\w/g, (letter) => letter.toUpperCase());

    const existingFields = (hostLogsTemplate?.fields ?? []).filter(
      (field): field is LogsTemplateField => field !== null
    );
    const fieldsByKey = new Map(existingFields.map((field) => [field.key ?? '', field]));

    const nextFields: LogsTemplateField[] = [...existingFields];

    for (let index = 0; index < equations.length; index += 1) {
      const equation = equations[index];
      const equationKey = equation.key?.trim();
      if (!equationKey) continue;

      const config = equationDerivedFieldConfigByIndex[index];

      const fieldUnit = config?.fieldUnit ?? SectionUnitsEnum.Na;
      const displayInLogAggregation = config?.displayInLogAggregation ?? true;

      const existing = fieldsByKey.get(equationKey);

      if (existing) {
        fieldsByKey.set(equationKey, {
          ...existing,
          fieldUnit,
          displayInLogAggregation,
          isDerived: true,
          derivedEquationKey: existing.derivedEquationKey ?? equationKey,
        });
        continue;
      }

      const newField: LogsTemplateField = {
        id: `newField_${Date.now()}_${index}`,
        key: equationKey,
        name: titleCaseFromKey(equationKey),
        type: TemplateFieldTypeEnum.Numeric,
        options: [],
        isReadOnly: true,
        isRequired: false,
        displayInReports: true,
        displayInReportFilters: false,
        displayInLogAggregation,
        isDerived: true,
        derivedEquationKey: equationKey,
        serverSide: false,
        fieldUnit,
        displayConfig: {
          id: `display_${Date.now()}_${index}`,
          textFormat: TextFormatEnum.None,
          isSortable: false,
          sortType: SortTypeEnum.String,
          type: TableCellStyleEnum.Text,
        },
      };

      fieldsByKey.set(equationKey, newField);
      nextFields.push(newField);
    }

    const updatedFields = nextFields.map((field) => fieldsByKey.get(field.key ?? '') ?? field);
    const cleanedFields = removeTypename(updatedFields);

    saveKpiConfig({
      variables: {
        input: {
          structureId,
          templateId: hostLogsTemplateId ?? '',
          lastModifiedFor: 'Update Card Config',
          equations: cleanedEquations,
          cardsConfig: cleanedCards,
          fields: cleanedFields,
        },
      },
      refetchQueries: [GetVersionsDocument, AllLogsTemplatesByTypeDocument, LogsTemplateDocument],
    });
  }, [
    cards,
    equations,
    equationDerivedFieldConfigByIndex,
    saveKpiConfig,
    structureId,
    hostLogsTemplateId,
    showSnackBar,
    hostLogsTemplate?.fields,
  ]);

  const handleTabChange = (selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  };

  const tabs = useMemo(() => {
    return [
      {
        index: 0,
        label: 'Equations',
        component: (
          <EquationsConfigTab
            mode={mode}
            equations={equations}
            availableFieldKeys={availableFieldKeys}
            availableFields={availableFieldsForEquations}
            indirectPobFieldKeys={indirectPobFieldKeySet}
            worklogFieldKeys={worklogFieldKeySet}
            costLogFieldKeys={costLogFieldKeySet}
            hostLogTemplateType={hostLogTemplateType}
            onEquationsChange={handleEquationsChange}
            onCreateNewField={handleCreateNewField}
            onEquationDerivedFieldConfigChange={(equationIndex, config) => {
              setEquationDerivedFieldConfigByIndex((prev) => ({
                ...prev,
                [equationIndex]: config,
              }));
            }}
            originalEquations={originalEquations}
          />
        ),
      },
      {
        index: 1,
        label: 'Cards Configuration',
        component: (
          <CardsConfigTab
            mode={mode}
            cards={cards}
            availableEquations={availableEquations}
            onCardsChange={handleCardsChange}
            originalCards={originalCards}
          />
        ),
      },
    ];
  }, [
    mode,
    equations,
    availableFieldKeys,
    handleEquationsChange,
    handleCreateNewField,
    originalEquations,
    cards,
    availableEquations,
    handleCardsChange,
    originalCards,
    availableFieldsForEquations,
    indirectPobFieldKeySet,
    worklogFieldKeySet,
    costLogFieldKeySet,
    hostLogTemplateType,
  ]);

  if (!hostLogsTemplateId) {
    return null;
  }

  if ((!cards || cards.length === 0) && (!equations || equations.length === 0))
    return (
      <Box sx={{ pt: 2 }}>
        <FieldPanel justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
          <Stack spacing={3} alignItems="center">
            <EmptyStatePlaceholder type="template" message="No KPI Configuration Found" />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Get started by creating your first KPI card or equation
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={createFirstCard}
                sx={containedButtonStyle}
              >
                Create First Card
              </Button>
              <Button variant="outlined" startIcon={<Add />} onClick={createFirstEquation}>
                Create First Equation
              </Button>
            </Stack>
          </Stack>
        </FieldPanel>
      </Box>
    );

  return (
    <Stack paddingTop={4}>
      <FormHeader>
        <Typography fontSize="2.2rem" fontWeight={500}>
          KPI Derived Fields
        </Typography>

        {mode === 'ReadOnly' ? (
          <Tooltip
            title="Edit Structure"
            placement="top"
            arrow
            slotProps={{
              popper: { sx: tooltipStyles },
            }}
          >
            <IconButton onClick={() => setMode('Edit')}>
              <Edit
                fontSize="inherit"
                sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
              />
            </IconButton>
          </Tooltip>
        ) : (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleSaveKpiConfig}
              disabled={isLoading}
            >
              {isLoading ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant="contained"
              sx={{ ...containedButtonStyle, fontSize: '12px', py: 1 }}
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </Stack>
        )}
      </FormHeader>

      <LineBreak marginBottom={2} />

      <BasicTabs
        tabs={tabs}
        selectedTab={selectedTab}
        changeTab={handleTabChange}
        tabsStyles={workpackTabStyles}
        customSeperator={<FloatingLine />}
        renderingStrategy="eager"
      />
    </Stack>
  );
};
